import { Alert, Button, Collapsible, Icon, Loader, Modal, OWCContext, Picker, t, Text, useComponentDidMount } from "owc-react-lib-common";
import React, { useContext } from "react";
import { Card } from 'src/components/Card';
import { Separator } from 'src/components/Separator';
import { Stepper } from 'src/components/Stepper';
import { Title } from "src/components/Title";
import { View } from "src/components/View";
import { useAlerts } from "src/hooks/useAlerts";
import * as CompetitionSchemeService from "src/services/CompetitionSchemeService";
import * as TeamService from "src/services/TeamService";
import * as GenerateCompetitionBySchemeService from "src/services/GenerateCompetitionBySchemeService";
import { ITeamLevelDTO } from "src/services/TeamLevelService";
import { ITeamDTO } from "src/services/TeamService";
import { SCHEMA_GROUP_TYPE_KNOCKOUT, SCHEMA_GROUP_TYPE_LEAGUE, SCHEMA_GROUP_TYPE_ONE_ROUND } from "../../../config";
import { ConfigGroupsApplySchema } from "./ConfigGroupsApplySchema";
import { Table } from "src/components/Table";

interface IStepperApplySchema {
  onClose: () => void;
  eventCategoryId: number;
  teamLevelId?: number;
  levels: ITeamLevelDTO[];
}

enum DistributionType {
  RANDOM = 1,
  BY_LEVEL = 2,
  MANUAL = 3,
}

export const StepperApplySchema = ({ onClose, eventCategoryId, teamLevelId, levels }: IStepperApplySchema) => {
  const alerts = useAlerts();
  const { colors } = useContext(OWCContext);
  const [loading, setLoading] = React.useState(true);
  const [schemas, setSchemas] = React.useState<any[]>();
  const [teams, setTeams] = React.useState<ITeamDTO[]>([]);
  const [teamsModal, setTeamsModal] = React.useState(undefined);
  const [openCollapsibleIndex, setOpenCollapsibleIndex] = React.useState(0); // Estado para controlar qué Collapsible está abierto
  const [step, setStep] = React.useState(0);
  const [selected, setSelected] = React.useState<any>(0);

  const [applySchemaData, setApplySchemaData] = React.useState<GenerateCompetitionBySchemeService.ApplySchemaDTO>({
    eventCategoryId,
    teamLevelId,
    schema: undefined,
    distributionModeId: undefined,
    calendarConfigs: undefined,
  });

  /**
   * component did mount
   */
  useComponentDidMount(async () => {
    try {
      const [schemas, teams] = await Promise.all([
        CompetitionSchemeService.getAllScheme(),
        TeamService.findByEventCategoryId(eventCategoryId),
      ]);

      setSchemas(schemas.data); // Guarda los datos en schemesData
      setTeams(teams.data);
    } catch (error) {
      alerts.createDSMError(error);
    }

    setLoading(false);
  })

  /**
   * next
   */
  const next = () => {
    setStep(prev => Math.max(prev, selected + 1));
    setSelected(prev => prev + 1);
  }

  /**
   * on select distribution type
   */
  const handledistributionTypeSelected = async () => {
    if (applySchemaData?.distributionModeId === DistributionType.BY_LEVEL && !applySchemaData?.teamLevelId) {
      return;
    }

    const calendarConfigs = applySchemaData?.schema?.groups?.map((group: any) => ({
      idSchemaGroup: group.idSchemaGroup,
      firstRoundDate: null,
      repeatType: null,
      repeat: null,
      bestOf: null,
      gamesOrder: ["L"],
      idType: parseInt(group.idType),
      groupName: group.groupName, // Usa el nombre del grupo o un valor predeterminado
    })) ?? [];

    setApplySchemaData(prev => ({ ...prev, calendarConfigs }))
    next();
  };

  /**
   * 
   */
  const showTeamsModal = (group) => {
    setTeamsModal(group);
  }

  /**
   * toggle team
   */
  const toggleTeam = (idSchemaGroup: number, object: ITeamDTO) => {
    applySchemaData.schema.groups = applySchemaData.schema.groups.map((obj) => {
      if (obj.idSchemaGroup !== idSchemaGroup) {
        return obj;
      }

      const group = applySchemaData.schema.groups.find((obj) => obj.idSchemaGroup === idSchemaGroup);

      const found = group.teams.find((team) => team.uuid === object.uuid);
      let teams;

      if (found) {
        teams = group.teams.filter((team) => team.uuid !== object.uuid);
      }
      else {
        teams = [...group.teams, object];
      }

      group.teams = teams;
      return group;
    });

    setApplySchemaData({ ...applySchemaData });
  }

  /**
   * 
   */
  const handleGroupSaved = (index: number) => {
    if (index + 1 < applySchemaData?.calendarConfigs?.length) {
      setOpenCollapsibleIndex(index + 1); // Abrir el siguiente Collapsible
    } else {
      setOpenCollapsibleIndex(null);
    }
  };

  /**
   * 
   */
  const handleGroupChange = (index: number, field: string | null, value: any) => {
    setApplySchemaData(prev => ({
      ...prev,
      calendarConfigs: prev?.calendarConfigs?.map((group, i) =>
        i === index
          ? field
            ? { ...group, [field]: value }
            : { ...group, ...value } // si field es null, actualiza todo
          : group
      )
    }));
  };

  /**
   * 
   */
  const onChangeSchemeSelectedData = (val: any) => {
    const selectedScheme = schemas?.find((scheme) => scheme.id == val);
    setApplySchemaData(prev => ({ ...prev, schema: selectedScheme }));
  };

  /**
   * 
   */
  const isGroupDataValid = (): boolean => {
    return applySchemaData?.calendarConfigs?.every((group) => {
      if (group.idType === SCHEMA_GROUP_TYPE_LEAGUE) {
        // Validar grupos de tipo 1 (liga)
        return (
          group.idSchemaGroup &&
          group.firstRoundDate &&
          group.repeatType &&
          group.repeat !== null &&
          group.repeat !== undefined &&
          group.groupName
        );
      }
      else if (group.idType === SCHEMA_GROUP_TYPE_KNOCKOUT) {
        // Validar grupos de tipo 2 (eliminatoria)
        return (
          group.idSchemaGroup &&
          group.groupName &&
          Array.isArray(group.bestOf) &&
          group.bestOf.length > 0 &&
          Array.isArray(group.gamesOrder) &&
          group.gamesOrder.length > 0
        );
      }
      else if (group.idType === SCHEMA_GROUP_TYPE_ONE_ROUND) {
        // Validar grupos de tipo 4 (jornada única)
        return (
          group.idSchemaGroup &&
          group.firstRoundDate &&
          group.repeatType &&
          group.repeat !== null &&
          group.repeat !== undefined &&
          group.groupName
        );
      }

      return false; // Si el tipo de grupo no es válido
    });
  };

  /**
   * save
   */
  const handleSave = async () => {
    if (!isGroupDataValid()) {
      return alerts.create({ text: t("admin.schema.stepperApplySchema.validation"), type: "error" });
    }

    try {
      await GenerateCompetitionBySchemeService.create(applySchemaData);
      // await GenerateCompetitionBySchemeService.create(eventCategoryId, schemeSelectedData?.id, distributionType, groupDataList);
      alerts.create({ text: t("admin.schema.stepperApplySchema.success"), type: "success" });
      onClose();
    } catch (error) {
      alerts.createDSMError(error);
    }
  };

  /**
   * return
   */
  const headers = [
    // { value: 1, label: t("admin.schema.stepperApplySchema.check") },
    { value: 2, label: t("admin.schema.stepperApplySchema.selectschema") },
    { value: 3, label: t("admin.schema.stepperApplySchema.selectdistributiontype") },
    { value: 4, label: t("admin.schema.stepperApplySchema.groupsettings") },
    { value: 5, label: t("admin.schema.stepperApplySchema.end") },
  ];

  return (
    <Modal visible title={t("admin.schema.stepperApplySchema.modal.title")} close={onClose} fullscreen>
      {loading ? <Loader /> : (
        <Stepper selected={selected} step={step} onPress={setSelected} headers={headers} containerStyle={{ marginBottom: 20 }}>
          {/* Paso 2: Seleccionamos el esquema */}
          <View>
            <View style={{ marginBottom: 20 }}>
              <Title containerStyle={{ width: 'auto', marginBottom: 5 }} text={t("admin.schema.stepperApplySchema.selectschema")}></Title>
              <Text>{t("admin.schema.stepperApplySchema.modal.selectschemadescription")}</Text>
            </View>

            <Picker
              label={t("admin.schema.stepperApplySchema.modal.selectScheme")}
              items={schemas?.map((scheme) => ({
                value: scheme.id,
                label: scheme.name,
              }))}
              onChange={(val) => onChangeSchemeSelectedData(val)}
              defaultOptionLabel={t("admin.schema.stepperApplySchema.modal.selectSchemePlaceholder")}
              required
            />

            {applySchemaData?.schema?.id && (
              <View flexDirection="row" alignItems="center" style={{ flexWrap: "wrap" }}>
                {applySchemaData?.schema?.groups?.map((group) => (
                  <View style={{ minWidth: 230 }}>
                    <Card key={group.idSchemaGroup} containerStyle={{ padding: 10 }}>
                      <Icon padding={0} marginRight={0} marginBottom={10} fontSize={50} type={group?.idType === SCHEMA_GROUP_TYPE_LEAGUE ? "users" : "cup"} />
                      <Text textAlign='center' bold>{group?.groupName}</Text>

                      <Separator marginTop={5} />

                      <Text>{t("admin.schema.stepperApplySchema.modal.idType")}: {group.idType === SCHEMA_GROUP_TYPE_LEAGUE ? t("admin.schema.stepperApplySchema.modal.league") : t("admin.schema.stepperApplySchema.modal.knockout")}</Text>
                      <Text>{t("admin.schema.stepperApplySchema.modal.numTeams")}: {group.numTeams}</Text>
                      <Text>{t("admin.schema.stepperApplySchema.modal.numRounds")}: {group.numRounds}</Text>
                      <Text>{t("admin.schema.stepperApplySchema.modal.standingVisible")}: {group.standingVisible ? t("global.yes") : t("global.no")}</Text>
                    </Card>
                  </View>
                ))}
              </View>
            )}

            <Button text={t("global.next")} disabled={!applySchemaData?.schema?.id} onPress={next} />
          </View>

          {/* Paso 3: Seleccionamos como distribuimos los equipos */}
          <View>
            <View style={{ marginBottom: 20 }}>
              <Title containerStyle={{ width: 'auto', marginBottom: 5 }} text={t("admin.schema.stepperApplySchema.selectdistributiontype")}></Title>
              <Text>{t("admin.schema.stepperApplySchema.modal.selectdistributiontypedescription")}</Text>
            </View>

            <Picker
              label={t("admin.schema.stepperApplySchema.modal.distributionType")}
              items={[
                // { value: DistributionType.RANDOM, label: t("admin.schema.stepperApplySchema.modal.random") },
                // { value: DistributionType.BY_LEVEL, label: t("admin.schema.stepperApplySchema.modal.byLevel") },
                { value: DistributionType.MANUAL, label: t("admin.schema.stepperApplySchema.modal.manual") },
              ]}
              onChange={(val) => setApplySchemaData(prev => ({ ...prev, distributionModeId: parseInt(val) }))}
              defaultOptionLabel={t("admin.schema.stepperApplySchema.modal.selectPlaceholder")}
              required
            />

            {applySchemaData?.distributionModeId === DistributionType.BY_LEVEL && (
              <Picker
                items={levels.map((level) => ({ label: level.name, value: level.id }))}
                defaultValue={teamLevelId}
                onChange={(value) => setApplySchemaData((prev) => ({ ...prev, teamLevelId: parseInt(value) }))}
              />
            )}

            {applySchemaData?.distributionModeId === DistributionType.MANUAL && (
              <View style={{ marginTop: 25 }}>
                {/* <Button text={t("admin.schema.stepperApplySchema.addTeam")} onPress={showTeamsModal} /> */}

                {applySchemaData?.schema?.groups?.map(group => {
                  return (
                    <View>
                      <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 15 }}>
                        <Title text={group.groupName} size="small" containerStyle={{ flex: 1, marginBottom: 0 }} />
                        <Icon type="plus" containerStyle={{ borderRadius: 5 }} backgroundColor={colors.primary.background} color="white" onPress={() => showTeamsModal(group)} />
                      </View>

                      <Table
                        pagination={false}
                        sortable={false}
                        data={group.teams}
                        headers={[
                          {
                            name: "name",
                            label: t("admin.schema.stepperApplySchema.team"),
                            flex: 1,
                            render: (object) => <Text>{!object.idSchemaGroupTeam ? object.name : `${t("admin.schema.stepperApplySchema.ficticious")} #${object.idSchemaGroupTeam}`}</Text>,
                          },
                          {
                            name: "assignedTeamLevelName",
                            label: t("admin.schema.stepperApplySchema.level"),
                            width: 160,
                          },
                          {
                            name: "actions",
                            label: "",
                            width: 60,
                            render: (object) => (
                              !object.idSchemaGroupTeam && <Icon type="trash" color="crimson" onPress={() => toggleTeam(group.idSchemaGroup, object)} />
                            )
                          },
                        ]}
                      />

                      <Modal visible={!!teamsModal} close={() => setTeamsModal(undefined)}>
                        <Table
                        defaultSize={25}
                          data={teams}
                          headers={[
                            { name: "name", label: t("admin.schema.stepperApplySchema.team"), flex: 1 },
                            { name: "assignedTeamLevelName", label: t("admin.schema.stepperApplySchema.level"), width: 160 },
                            {
                              name: "actions", label: "", width: 60, render: (team) => {
                                const isAdded = !!teamsModal?.teams.find((obj) => obj.uuid === team.uuid);
                                const disabled = !isAdded && teamsModal?.teams?.length >= teamsModal?.numTeams;
                                const addedSomewhereElse = applySchemaData?.schema?.groups?.find((obj) => obj.idSchemaGroup !== teamsModal?.idSchemaGroup && obj.teams.some((obj) => obj.id === team.id))

                                return (
                                  <View style={{ alignItems: "flex-end" }}>
                                    <Icon type={isAdded ? "remove" : "add"} color={isAdded ? "crimson" : disabled ? "#eee" : addedSomewhereElse ? "orange" : "teal"} onPress={!disabled && (() => toggleTeam(teamsModal.idSchemaGroup, team))} />
                                  </View>
                                );
                              },
                            }
                          ]} />
                      </Modal>
                    </View>
                  );
                })}
              </View>
            )}

            <Button text={t("global.next")} disabled={!applySchemaData?.distributionModeId} onPress={handledistributionTypeSelected} />
          </View>

          {/* Paso 4: Configuramos los grupos */}
          <View>
            <View style={{ marginBottom: 20 }}>
              <Title containerStyle={{ width: 'auto', marginBottom: 5 }} text={t("admin.schema.stepperApplySchema.groupsettings")}></Title>
              <Text>{t("admin.schema.stepperApplySchema.modal.groupsettingsdescription")}</Text>
            </View>

            {applySchemaData?.calendarConfigs?.map((group, index) => (
              <Collapsible
                headerStyle={{ padding: 7, marginBottom: 0 }}
                iconOpen={'arrow-down'}
                iconClose={'arrow-up'}
                borderWidthIcon={0}
                titleSize="xsmall"
                headerBottom={null}
                key={index}
                title={group?.groupName}
                trigger={{ value: openCollapsibleIndex === index ? "open" : "close", key: index }}
                onToggle={(value) => value === "open" && setOpenCollapsibleIndex(index)}
              >
                <ConfigGroupsApplySchema
                  group={group}
                  onSave={() => handleGroupSaved(index)}
                  onGroupChange={(field, value) => handleGroupChange(index, field, value)}
                />
              </Collapsible>
            ))}

            <Button text={t("global.next")} onPress={handleSave} />
          </View>

          {/* Paso 5: fin */}
          <View />
        </Stepper>
      )}
    </Modal>
  );
};